import React from 'react';

function parseDate(d: any): Date | null {
  if (!d) return null;
  return d instanceof Date ? d : new Date(d);
}

export type ChallengeCountdownState = 'upcoming' | 'live' | 'none';

export interface ChallengeCountdownResult {
  countdown: string;
  state: ChallengeCountdownState;
}

/**
 * Determines countdown target using the `status` field from the API:
 *
 * - status === 'active'                          → "live":     count down to endDate
 * - status === 'upcoming' | 'viewable_soon'      → "upcoming": count down to startDate
 * - status === 'expired' or missing              → "none":     hide timer
 *
 * The challenge must also be featured (featured === true) to show a countdown.
 */
export function getChallengeCountdownTarget(challenge: any): {
  target: Date | null;
  state: ChallengeCountdownState;
} {
  if (!challenge) return { target: null, state: 'none' };

  // Items from the featuredArray are already filtered as featured by the API,
  // but we double-check if the field is present.
  const isFeatured = challenge.featured === undefined ? true : Boolean(challenge.featured);
  if (!isFeatured) return { target: null, state: 'none' };

  const status: string = challenge.status ?? '';
  const start = parseDate(challenge.startDate);
  const end = parseDate(challenge.endDate);

  if (status === 'active') {
    return { target: end, state: 'live' };
  }

  if (status === 'upcoming' || status === 'viewable_soon') {
    return { target: start, state: 'upcoming' };
  }

  return { target: null, state: 'none' };
}

function formatRemaining(remaining: number): string {
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((remaining / (1000 * 60)) % 60);

  return `${days} days : ${String(hours).padStart(2, '0')} hours : ${String(minutes).padStart(2, '0')} min`;
}

export default function useChallengeCountdown(challenge: any): ChallengeCountdownResult {
  const [result, setResult] = React.useState<ChallengeCountdownResult>({
    countdown: '—',
    state: 'none',
  });

  React.useEffect(() => {
    const { target, state } = getChallengeCountdownTarget(challenge);

    if (!target || state === 'none') {
      setResult({ countdown: '—', state: 'none' });
      return;
    }

    const targetTime = target.getTime();

    const tick = () => {
      const remaining = targetTime - Date.now();
      if (remaining <= 0) {
        setResult({ countdown: '—', state: 'none' });
        return false;
      }
      setResult({ countdown: formatRemaining(remaining), state });
      return true;
    };

    tick();
    const id = window.setInterval(() => {
      const cont = tick();
      if (!cont) window.clearInterval(id);
    }, 1000);

    return () => window.clearInterval(id);
  }, [challenge]);

  return result;
}
